import { z } from 'zod/v3';

const twoBytesSchema = z.coerce
  .number()
  .int()
  .min(0, { message: 'O valor deve ser no mínimo 0' })
  .max(65535, { message: 'O valor deve ser no máximo 65535' });

const oneBytesSchema = z.coerce
  .number()
  .int()
  .min(0, { message: 'O valor deve ser no mínimo 0' })
  .max(255, { message: 'O valor deve ser no máximo 255' });

const password = z
  .object({
    old: z.string().optional(),
    new: z.string().optional(),
  })
  .optional();

const sleep = z.coerce
  .number()
  .positive({ message: 'O valor deve ser positivo' })
  .min(1, { message: 'O valor deve ser no mínimo 1' })
  .max(5, { message: 'O valor deve ser no máximo 5' })
  .optional();

const sensitivity_adjustment = z.coerce
  .number()
  .positive({ message: 'O valor deve ser positivo' })
  .min(30, { message: 'O valor deve ser no mínimo 30' })
  .max(1000, { message: 'O valor deve ser no máximo 1000' })
  .optional();

const keepAliveSchema = z.coerce
  .number()
  .positive({ message: 'O valor deve ser positivo' })
  .min(60, { message: 'O valor deve ser no mínimo 60' })
  .max(1800, { message: 'O valor deve ser no máximo 1800' })
  .optional();

const odometer = z.coerce.number().min(0, { message: 'O valor deve ser no mínimo 0' }).optional();

const angle_adjustment = z.coerce
  .number()
  .positive({ message: 'O valor deve ser positivo' })
  .min(5, { message: 'O valor deve ser no mínimo 5' })
  .max(90, { message: 'O valor deve ser no máximo 90' })
  .optional();

const ack = z.coerce
  .number()
  .min(0, { message: 'O valor deve ser no mínimo 0' })
  .max(180, { message: 'O valor deve ser no máximo 180' })
  .optional();

// Esquemas básicos
const dnsSchema = z.object({
  address: z.string(),
  port: twoBytesSchema,
});

const ipSchema = z.object({
  ip: z
    .string()
    .regex(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
      'Endereço IP inválido',
    ),
  port: twoBytesSchema,
});

const apnSchema = z.object({
  address: z.string(),
  user: z.string(),
  password: z.string().optional(),
});

// Esquema para Configuração Geral
export const generalConfigSchema = z.object({
  ip_primary: ipSchema.optional(),
  ip_secondary: ipSchema.optional(),
  dns_primary: dnsSchema.optional(),
  dns_secondary: dnsSchema.optional(),
  apn: apnSchema.optional(),
  data_transmission_on: z.coerce.number().optional(),
  data_transmission_off: z.coerce.number().optional(),
  keep_alive: keepAliveSchema.optional(),
});

// Esquema para E3Plus
export const e3PlusConfigSchema = z.object({
  password: password,
  timezone: z.number().optional(),
  lock_type: z.coerce.number().optional(),
  odometer: odometer,
  accelerometer_sensitivity: z.coerce.number().optional(),
  economy_mode: z.coerce.number().optional(),
  sensitivity_adjustment: sensitivity_adjustment,
  lbs_position: z.coerce.boolean().optional().default(false),
  cornering_position_update: z.coerce.boolean().optional().default(false),
  ignition_alert_power_cut: z.coerce.boolean().optional().default(false),
  gprs_failure_alert: z.coerce.boolean().optional().default(false),
  led: z.coerce.boolean().optional().default(false),
  virtual_ignition: z.coerce.boolean().optional().default(false),
  work_mode: z.string().optional(),
  operation_mode: z.coerce.boolean().optional(),
  max_speed: oneBytesSchema.optional(),
  sleep: sleep,
});

// Esquema para E3Plus4G
export const e3Plus4GConfigSchema = z.object({
  password: password,
  timezone: z.coerce.number().optional(),
  lock_type: z.coerce.number().optional(),
  odometer: odometer,
  economy_mode: z.coerce.number().optional(),
  sensitivity_adjustment: sensitivity_adjustment,
  lbs_position: z.coerce.boolean().optional().default(false),
  cornering_position_update: z.coerce.boolean().optional().default(false),
  led: z.coerce.boolean().optional().default(false),
  virtual_ignition: z.coerce.boolean().optional().default(false),
  virtual_ignition_by_voltage: z.coerce.boolean().optional().default(false),
  virtual_ignition_by_movement: z.coerce.boolean().optional().default(false),
  anti_theft: z.coerce.boolean().optional().default(false),
  jammer_detection: z.coerce.boolean().optional().default(false),
  max_speed: oneBytesSchema.optional(),
  communication_type: z.string().optional(),
  protocol_type: z.string().optional(),
  horimeter: twoBytesSchema.optional(),
  clear_buffer: z.coerce.boolean().optional(),
  clear_horimeter: z.coerce.boolean().optional(),
  input_1: z.number().optional(),
  input_2: z.number().optional(),
  angle_adjustment: angle_adjustment,
  lock_type_progression: z
    .object({
      n1: twoBytesSchema,
      n2: twoBytesSchema,
    })
    .optional(),
  ignition_by_voltage: z
    .object({
      initial: twoBytesSchema,
      final: twoBytesSchema,
    })
    .refine((data) => data.initial !== undefined && data.final !== undefined, {
      message: 'VION e VIOFF devem ser preenchidos.',
      path: ['initial'],
    })
    .refine(
      (data) => {
        // biome-ignore lint/style/noNonNullAssertion: values validated by schema
        return data.initial! > data.final!;
      },
      {
        message: 'VION deve ser maior do que VIOFF.',
        path: ['initial'],
      },
    )
    .optional(),
  ack: ack,
  ignition_status_hb: z.coerce.boolean().optional().default(true),
});

// Esquema para NB2
export const nb2ConfigSchema = z.object({
  data_transmission_event: twoBytesSchema.optional(),
  odometer,
  time_to_sleep: twoBytesSchema.optional(),
  virtual_ignition_12v: z
    .object({
      initial: twoBytesSchema,
      final: twoBytesSchema,
    })
    .refine((data) => data.initial !== undefined && data.final !== undefined, {
      message: 'Os intervalos devem ser preenchidos.',
      path: ['initial'],
    })
    .refine((data) => data.initial < data.final, {
      message: 'O valor final deve ser maior do que inicial.',
      path: ['initial'],
    })
    .optional(),
  virtual_ignition_24v: z
    .object({
      initial: twoBytesSchema,
      final: twoBytesSchema,
    })
    .refine((data) => data.initial !== undefined && data.final !== undefined, {
      message: 'Os intervalos devem ser preenchidos.',
      path: ['initial'],
    })
    .refine((data) => data.initial < data.final, {
      message: 'O valor final deve ser maior do que inicial.',
      path: ['initial'],
    })
    .optional(),
  lock_type: z.coerce.number().optional(),
  economy_mode: z.coerce.number().optional(),
  heading_detection_angle: z.coerce.number().min(0).max(180).optional(),
  speed_alert_threshold: oneBytesSchema.optional(),
  accel_threshold_for_ignition_on: oneBytesSchema.optional(),
  accel_threshold_for_ignition_off: oneBytesSchema.optional(),
  accel_threshold_for_movement: twoBytesSchema.optional(),
  harsh_acceleration_threshold: twoBytesSchema.optional(),
  harsh_braking_threshold: twoBytesSchema.optional(),
  input_1: z.coerce.number().optional(),
  input_2: z.coerce.number().optional(),
  input_3: z.coerce.number().optional(),
  input_4: z.coerce.number().optional(),
});

// Esquema para NB2 LoRa (NB2 + parâmetros LoRa)
export const nb2LoraConfigSchema = nb2ConfigSchema.extend({
  lorawan_data_transmission_event: twoBytesSchema.optional(),
  p2p_data_transmission_event: twoBytesSchema.optional(),
  wtr: z
    .object({
      a: twoBytesSchema.optional(),
      b: twoBytesSchema.optional(),
      c: twoBytesSchema.optional(),
      d: twoBytesSchema.optional(),
    })
    .optional(),
});

// Esquema para Lora
export const loraConfigSchema = z.object({
  data_transmission_sleep: twoBytesSchema.optional(),
  data_transmission_position: twoBytesSchema.optional(),
  lorawan_mode_duration: twoBytesSchema.optional(),
  data_transmission_status: twoBytesSchema.optional(),
  lorawan_data_transmission_event: twoBytesSchema.optional(),
  p2p_mode_duration: twoBytesSchema.optional(),
  p2p_data_transmission_event: twoBytesSchema.optional(),
  odometer,
  activation_type: z.enum(['00', '01']).optional(),
  virtual_ignition_12v: z
    .object({
      initial: twoBytesSchema,
      final: twoBytesSchema,
    })
    .refine((data) => data.initial !== undefined && data.final !== undefined, {
      message: 'Os intervalos devem ser preenchidos.',
      path: ['initial'],
    })
    .refine((data) => data.initial < data.final, {
      message: 'O valor final deve ser maior do que inicial.',
      path: ['initial'],
    })
    .optional(),
  virtual_ignition_24v: z
    .object({
      initial: twoBytesSchema,
      final: twoBytesSchema,
    })
    .refine((data) => data.initial !== undefined && data.final !== undefined, {
      message: 'Os intervalos devem ser preenchidos.',
      path: ['initial'],
    })
    .refine((data) => data.initial < data.final, {
      message: 'O valor final deve ser maior do que inicial.',
      path: ['initial'],
    })
    .optional(),
  heading: z.boolean().default(false),
  heading_event_mode: z.boolean().default(false),
  heading_detection_angle: z.coerce.number().min(0).max(180).optional(),
  speed_alert_threshold: oneBytesSchema.optional(),
  accel_threshold_for_ignition_on: oneBytesSchema.optional(),
  accel_threshold_for_ignition_off: oneBytesSchema.optional(),
  accel_threshold_for_movement: twoBytesSchema.optional(),
  harsh_acceleration_threshold: twoBytesSchema.optional(),
  harsh_braking_threshold: twoBytesSchema.optional(),

  full_configuration_table: z.string().optional(),
  full_functionality_table: z.string().optional(),
  fifo_send_and_hold_times: z.string().optional(),
  mcu_configuration: z.string().optional(),
  output_table: z.string().optional(),
  input_1: z.coerce.number().optional(),
  input_2: z.coerce.number().optional(),
  input_3: z.coerce.number().optional(),
  input_4: z.coerce.number().optional(),
  input_5: z.coerce.number().optional(),
  input_6: z.coerce.number().optional(),
  led_lighting: z.string().optional(),
});

// Esquema para BWS 4G
export const bws4GConfigSchema = z.object({
  password: password,
  lock_type: z.coerce.number().optional(),
  lock_type_variation: z.coerce.number().optional(),
  odometer: odometer,
  sensitivity_adjustment: sensitivity_adjustment,
  lbs_position: z.coerce.boolean().optional().default(false),
  led: z.coerce.boolean().optional().default(false),
  anti_theft: z.coerce.boolean().optional().default(false),
  jammer_detection: z.coerce.boolean().optional().default(false),
  max_speed: oneBytesSchema.optional(),
  communication_type: z.string().optional(),
  protocol_type: z.string().optional(),
  horimeter: twoBytesSchema.optional(),
  clear_buffer: z.coerce.boolean().optional(),
  clear_horimeter: z.coerce.boolean().optional(),
  input_1: z.coerce.number().optional(),
  input_2: z.coerce.number().optional(),
  angle_adjustment: angle_adjustment,
  lock_type_progression: z
    .object({
      n1: twoBytesSchema,
      n2: twoBytesSchema,
    })
    .optional(),
  ignition_status_hb: z.coerce.boolean().optional().default(true),
  sms: z.coerce.boolean().optional().default(false),
  gps_filter_speed: z.coerce.boolean().optional().default(false),
  gps_periodic_wakeup: z.coerce.boolean().optional().default(false),
  always_get_agps: z.coerce.boolean().optional().default(false),
  ignition_type: z.coerce.number().optional(),
  economy_mode: z.coerce.number().optional(),
  network_mode: z.enum(['AUTO', '4G', '2G']).optional(),
  gps_replay_fix_sleep: z.coerce.boolean().optional().default(false),
  improper_movement: z.coerce.boolean().optional().default(false),
});

// Esquema para BWS 4G+LoRa (cópia do BWS 4G — vai divergir quando a parte LoRa for implementada)
export const bws4GLoraConfigSchema = z.object({
  password: password,
  lock_type: z.coerce.number().optional(),
  lock_type_variation: z.coerce.number().optional(),
  odometer: odometer,
  sensitivity_adjustment: sensitivity_adjustment,
  lbs_position: z.coerce.boolean().optional().default(false),
  led: z.coerce.boolean().optional().default(false),
  anti_theft: z.coerce.boolean().optional().default(false),
  jammer_detection: z.coerce.boolean().optional().default(false),
  max_speed: oneBytesSchema.optional(),
  communication_type: z.string().optional(),
  protocol_type: z.string().optional(),
  horimeter: twoBytesSchema.optional(),
  clear_buffer: z.coerce.boolean().optional(),
  clear_horimeter: z.coerce.boolean().optional(),
  input_1: z.coerce.number().optional(),
  input_2: z.coerce.number().optional(),
  angle_adjustment: angle_adjustment,
  lock_type_progression: z
    .object({
      n1: twoBytesSchema,
      n2: twoBytesSchema,
    })
    .optional(),
  ignition_status_hb: z.coerce.boolean().optional().default(true),
  sms: z.coerce.boolean().optional().default(false),
  gps_filter_speed: z.coerce.boolean().optional().default(false),
  gps_periodic_wakeup: z.coerce.boolean().optional().default(false),
  always_get_agps: z.coerce.boolean().optional().default(false),
  ignition_type: z.coerce.number().optional(),
  economy_mode: z.coerce.number().optional(),
  network_mode: z.enum(['AUTO', '4G', '2G']).optional(),
  gps_replay_fix_sleep: z.coerce.boolean().optional().default(false),
  improper_movement: z.coerce.boolean().optional().default(false),
});

// Campo vazio no formulário vira undefined (em vez de 0 via coerce) — assim o comando não é enviado.
const emptyToUndefined = (value: unknown) =>
  value === '' || value === null || (typeof value === 'number' && Number.isNaN(value))
    ? undefined
    : value;

const llNbMinutesSchema = z.preprocess(emptyToUndefined, twoBytesSchema.optional());

const llNbPositiveMinutesSchema = z.preprocess(
  emptyToUndefined,
  twoBytesSchema.pipe(z.number().min(1, { message: 'O valor deve ser no mínimo 1' })).optional(),
);

const llNbRadioBehaviorSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== 'object') return undefined;
    const entries = Object.values(value as Record<string, unknown>);
    return entries.every((v) => emptyToUndefined(v) === undefined) ? undefined : value;
  },
  z
    .object({
      no_connection_timeout: twoBytesSchema.pipe(
        z.number().min(1, { message: 'O valor deve ser no mínimo 1' }),
      ),
      stopped_cycle: twoBytesSchema.pipe(
        z.number().min(1, { message: 'O valor deve ser no mínimo 1' }),
      ),
      moving_cycle: twoBytesSchema.pipe(
        z.number().min(1, { message: 'O valor deve ser no mínimo 1' }),
      ),
      flags: z.coerce
        .number()
        .refine((v) => [37, 41, 45].includes(v), { message: 'Selecione uma opção válida' }),
    })
    .optional(),
);

// Esquema para BWS LL NB IoT+LoRa (manual v1.0.0)
export const llNbConfigSchema = z.object({
  data_transmission_moving: llNbPositiveMinutesSchema, // WCM (min)
  data_transmission_stopped: llNbPositiveMinutesSchema, // WCW (min)
  data_transmission_sos: llNbMinutesSchema, // WCE (seg)
  data_transmission_stock: llNbMinutesSchema, // WCTE (min)
  data_transmission_disabled: llNbMinutesSchema, // WCD (min)
  gps_every_cycle: z.boolean().optional(), // WFGO
  gps_fix_timeout: llNbMinutesSchema, // WCG (seg)
  work_mode: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .refine((v) => [1, 2, 128].includes(v), { message: 'Selecione um modo válido' })
      .optional(),
  ), // WFOM
  sos_timeout: llNbMinutesSchema, // WFST (min)
  power_string: z.boolean().optional(), // WFPO
  radio_behavior: llNbRadioBehaviorSchema, // WBTR
});

export type TechnologySystemName =
  | 'DM_E3_PLUS'
  | 'DM_E3_PLUS_4G'
  | 'DM_BWS_NB2'
  | 'DM_BWS_NB2_LORA'
  | 'DM_BWS_LORA'
  | 'DM_BWS_4G'
  | 'DM_BWS_PLUS_4G_LORA'
  | 'DM_BWS_LL_NB'
  | 'DM_BWS_LL_NB_LORA'
  | 'DM_BWS_BAIT_NB'
  | 'DM_BWS_BAIT_NB_LORA';

export const configProfileSchema = z.object({
  id: z.string().optional(),
  client_id: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
  manual_client: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
  manual_client_register: z.boolean().default(true),
  technology_id: z.string(),
  technology_system_name: z.enum([
    'DM_E3_PLUS',
    'DM_E3_PLUS_4G',
    'DM_BWS_NB2',
    'DM_BWS_NB2_LORA',
    'DM_BWS_LORA',
    'DM_BWS_4G',
    'DM_BWS_PLUS_4G_LORA',
    'DM_BWS_LL_NB',
    'DM_BWS_LL_NB_LORA',
    'DM_BWS_BAIT_NB',
    'DM_BWS_BAIT_NB_LORA',
  ]),
  name: z.string().min(1),
  type: z.enum([
    'HUMAN',
    'TRUCK',
    'MOTORCYCLE',
    'STUFF',
    'BOAT',
    'CAR',
    'BUS',
    'UTILITY_VEHICLE',
    'BIKE',
    'ANIMAL',
    'ROAD_IMPLEMENT',
    'FARM_IMPLEMENT',
    'JET',
    'JET_SKI',
    'AIRCRAFT',
    'OTHER',
  ]),
  config: z.object({
    general: generalConfigSchema.optional(),
    specific: z
      .discriminatedUnion('technology_system_name', [
        e3Plus4GConfigSchema.extend({
          technology_system_name: z.literal('DM_E3_PLUS_4G'),
        }),
        e3PlusConfigSchema.extend({
          technology_system_name: z.literal('DM_E3_PLUS'),
        }),
        nb2ConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_NB2'),
        }),
        nb2LoraConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_NB2_LORA'),
        }),
        loraConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_LORA'),
        }),
        bws4GConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_4G'),
        }),
        bws4GLoraConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_PLUS_4G_LORA'),
        }),
        llNbConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_LL_NB'),
        }),
        // Mesmos comandos do BWS LL NB (manual BWS LL NB+LoRa v1.0.0)
        llNbConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_LL_NB_LORA'),
        }),
        llNbConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_BAIT_NB'),
        }),
        llNbConfigSchema.extend({
          technology_system_name: z.literal('DM_BWS_BAIT_NB_LORA'),
        }),
      ])
      .optional(),
  }),
});

export type ConfigurationProfileSchema = z.infer<typeof configProfileSchema>;
